import { t } from "@lingui/core/macro";
import type { LayoutChangeEvent } from "react-native";

import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import type { LoyaltyReward } from "../LoyaltyRewardsModal";
import { RewardPointsPill } from "./RewardPointsPill";

type RewardRowProps = {
  reward: LoyaltyReward;
  pointTerminologyOne: string;
  pointTerminologyOther: string;
  readOnly: boolean;
  onSelectReward?: (reward: LoyaltyReward) => void;
  pointsPillWidth: number | undefined;
  onPointsPillLayout: (event: LayoutChangeEvent) => void;
};

export const RewardRow = ({
  reward,
  pointTerminologyOne,
  pointTerminologyOther,
  readOnly,
  onSelectReward,
  pointsPillWidth,
  onPointsPillLayout,
}: RewardRowProps) => {
  const isAvailable = reward.status === "AVAILABLE";
  const isSelectable = isAvailable && !readOnly && !!onSelectReward;
  const content = (
    <HStack alignItems="stretch" width="100%">
      <Box justifyContent="center">
        <RewardPointsPill
          points={reward.points}
          isAvailable={isAvailable}
          width={pointsPillWidth}
          onLayout={onPointsPillLayout}
        />
      </Box>
      <VStack flex={1} justifyContent="center" px={4} py={2}>
        <Text
          testID={`loyalty-reward-${reward.id}-name`}
          variant="mainSmallSemiBold"
          color={isAvailable ? "text-primary" : "text-secondary"}
        >
          {reward.name}
        </Text>
      </VStack>
    </HStack>
  );
  const pointTerminology =
    reward.points === 1 ? pointTerminologyOne : pointTerminologyOther;
  const accessibilityLabel = t`${reward.name}, ${reward.points} ${pointTerminology}`;

  if (isSelectable) {
    return (
      <Pressable
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="button"
        testID={`loyalty-reward-${reward.id}`}
        onPress={() => onSelectReward(reward)}
        borderRadius="2"
        backgroundColor="surface-primary"
        overflow="hidden"
        _hover={{ backgroundColor: "surface-secondary" }}
        _pressed={{ backgroundColor: "surface-secondary" }}
      >
        {content}
      </Pressable>
    );
  }

  return (
    <HStack
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled: true }}
      testID={`loyalty-reward-${reward.id}`}
      borderRadius="2"
      backgroundColor="surface-primary"
      overflow="hidden"
      opacity={isAvailable ? 1 : 0.6}
    >
      {content}
    </HStack>
  );
};
